import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildHowTo, buildBreadcrumb } from "@/lib/seo";
import { Check } from "lucide-react";

export default function HowItWorks() {
  return (
    <div>
      <Seo
        title="How It Works"
        description="Submit your property, see possible solution paths, receive offers from verified providers, compare side-by-side, and choose. Free for sellers."
        path="/how-it-works"
        jsonLd={[
          buildHowTo(
            "How to use ADN Marketplace as a distressed property seller",
            "Five steps to compare cash offers, listing proposals, and creative-financing options before selling.",
            [
              { name: "Tell us about your property", text: "Submit address, situation, financials, and photos. AI fills the gaps." },
              { name: "See possible paths", text: "We surface fast cash, list for top dollar, foreclosure help, probate, repair-and-list, and creative financing options." },
              { name: "Receive offers", text: "Verified investors send cash and creative offers. Realtors send listing proposals. Lenders, attorneys, contractors send what fits." },
              { name: "Compare side by side", text: "Offer amount, closing timeline, conditions, estimated net to you. AI generates a neutral comparison." },
              { name: "Chat, schedule, decide", text: "Message providers in-app, schedule walkthroughs, and pick what is right for you." },
            ]
          ),
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "How It Works", url: "https://adn-marketplace.com/how-it-works" },
          ]),
        ]}
      />
      <Navbar />
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <p className="eyebrow text-[var(--adn-gold)]">How it works</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light mt-3 max-w-3xl">A neutral way to compare every realistic option.</h1>
          <p className="mt-6 text-white/75 max-w-2xl text-lg leading-relaxed">
            ADN is not a buyer, a lender, or a brokerage. We are a marketplace. We organize the options — you make the decision.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 py-24">
        {[
          { n: "01", t: "Tell us about your property", d: "Address, situation, basic financials, photos. AI fills the gaps with smart follow-up questions so nothing is missed." },
          { n: "02", t: "We surface your possible paths", d: "Fast cash sale, list for top dollar, foreclosure help, repair-and-list, creative financing, probate referrals — we lay them all out." },
          { n: "03", t: "Verified providers respond", d: "Investors send cash and creative offers. Realtors send listing proposals. Lenders, attorneys, and contractors send what makes sense for your situation." },
          { n: "04", t: "Compare side by side", d: "Offer amount, closing timeline, conditions, estimated net to you. AI generates a neutral comparison." },
          { n: "05", t: "Chat, schedule, decide", d: "Talk directly with providers in-app. Schedule walkthroughs. Pick the option that's right for you." },
        ].map((s, i) => (
          <div key={i} className="grid grid-cols-12 gap-8 py-8 border-b border-[var(--adn-line)] last:border-b-0">
            <div className="col-span-12 md:col-span-2 text-[var(--adn-gold)] font-display text-5xl font-light tabular">{s.n}</div>
            <div className="col-span-12 md:col-span-10">
              <h3 className="font-display text-2xl font-semibold">{s.t}</h3>
              <p className="mt-2 text-[var(--adn-text-soft)] text-lg leading-relaxed">{s.d}</p>
            </div>
          </div>
        ))}

        <div className="mt-14 bg-white border border-[var(--adn-line)] p-8">
          <p className="eyebrow">What it costs</p>
          <h3 className="font-display text-2xl font-semibold mt-2">Sellers: $0. Providers: pay only for access.</h3>
          <ul className="mt-4 space-y-2 text-[var(--adn-text-soft)]">
            <li className="flex gap-2"><Check size={18} className="text-[var(--adn-emerald)] mt-0.5" /> Free seller submission and dashboard</li>
            <li className="flex gap-2"><Check size={18} className="text-[var(--adn-emerald)] mt-0.5" /> Provider plans starting at $97/mo</li>
            <li className="flex gap-2"><Check size={18} className="text-[var(--adn-emerald)] mt-0.5" /> No commissions taken on closed deals (MVP)</li>
          </ul>
          <div className="mt-6 flex gap-3">
            <Link to="/submit-property" className="btn-gold px-6 py-3 rounded text-sm">Submit My Property</Link>
            <Link to="/pricing" className="btn-outline-navy px-6 py-3 rounded text-sm">Provider Pricing</Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
